import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Calendar, Receipt, ChevronRight, ChevronLeft, Plus, X, Bell, CheckCircle, AlertCircle, Smartphone, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;

// Helper functions for push notifications
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [balance, setBalance] = useState('');
  const [payFrequency, setPayFrequency] = useState('monthly');
  const [nextPayday, setNextPayday] = useState('');
  const [bills, setBills] = useState([]);
  const [newBill, setNewBill] = useState({ name: '', amount: '', due_date: '' });
  
  // Notification state
  const [notificationStep, setNotificationStep] = useState('initial'); // 'initial', 'checking', 'blocked', 'success', 'skipped'
  const [isIOS] = useState(() => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

  const { updateUser } = useAuth();
  const navigate = useNavigate();

  const totalSteps = 5;

  const addBill = () => {
    if (!newBill.name || !newBill.amount || !newBill.due_date) {
      toast.error('Please fill in all bill fields');
      return;
    }

    setBills([...bills, {
      name: newBill.name,
      amount: parseFloat(newBill.amount),
      due_date: parseInt(newBill.due_date),
      is_recurring: true,
      category: null
    }]);
    setNewBill({ name: '', amount: '', due_date: '' });
  };

  const removeBill = (index) => {
    setBills(bills.filter((_, i) => i !== index));
  };

  const handleComplete = async () => {
    if (!balance || !nextPayday) {
      toast.error('Please complete all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          balance: parseFloat(balance),
          pay_frequency: payFrequency,
          next_payday: nextPayday,
          bills
        })
      });

      if (!response.ok) {
        throw new Error('Failed to complete onboarding');
      }

      updateUser({ onboarded: true });
      toast.success('Setup complete!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    console.log('[Onboarding] Enabling notifications...');
    setNotificationStep('checking');
    
    try {
      // Check if push is supported
      if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('[Onboarding] Push not supported');
        setNotificationStep('blocked');
        return;
      }
      
      // Request permission
      const permission = await Notification.requestPermission();
      console.log('[Onboarding] Permission result:', permission);
      
      if (permission !== 'granted') {
        setNotificationStep('blocked');
        return;
      }
      
      // Get VAPID key
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        console.error('[Onboarding] VAPID key not set');
        setNotificationStep('blocked');
        return;
      }
      
      // Get service worker
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      }
      
      // Save to backend
      const response = await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        })
      });
      
      if (response.ok) {
        console.log('[Onboarding] Notifications enabled!');
        setNotificationStep('success');
      } else {
        throw new Error('Backend save failed');
      }
    } catch (error) {
      console.error('[Onboarding] Notification error:', error);
      setNotificationStep('blocked');
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return balance !== '';
      case 2: return payFrequency && nextPayday;
      case 3: return true; // Bills are optional
      case 4: return true; // Summary
      case 5: return notificationStep === 'success' || notificationStep === 'skipped' || notificationStep === 'blocked';
      default: return false;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#63E6E2]/10 flex items-center justify-center">
                <Wallet className="w-8 h-8 text-[#63E6E2]" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">Current Balance</h2>
              <p className="text-[#9CA3AF]">How much do you have right now?</p>
            </div>

            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Available Balance</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280] text-lg">£</span>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                  className="pl-8 h-14 text-2xl font-heading bg-[#1B1F3A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2]"
                  data-testid="balance-input"
                />
              </div>
              <p className="text-[#6B7280] text-sm">Enter the amount in your bank account</p>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#63E6E2]/10 flex items-center justify-center">
                <Calendar className="w-8 h-8 text-[#63E6E2]" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">Pay Schedule</h2>
              <p className="text-[#9CA3AF]">When do you get paid?</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Pay Frequency</Label>
                <Select value={payFrequency} onValueChange={setPayFrequency}>
                  <SelectTrigger className="h-12 bg-[#1B1F3A] border-[#2D3354] text-white" data-testid="pay-frequency-select">
                    <SelectValue placeholder="How often do you get paid?" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1B1F3A] border-[#2D3354]">
                    <SelectItem value="weekly" className="text-white hover:bg-white/10">Weekly</SelectItem>
                    <SelectItem value="biweekly" className="text-white hover:bg-white/10">Bi-weekly (Every 2 weeks)</SelectItem>
                    <SelectItem value="lunar" className="text-white hover:bg-white/10">Lunar (Every 4 weeks / 28 days)</SelectItem>
                    <SelectItem value="monthly" className="text-white hover:bg-white/10">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Next Payday</Label>
                <Input
                  type="date"
                  value={nextPayday}
                  onChange={(e) => setNextPayday(e.target.value)}
                  className="h-12 bg-[#1B1F3A] border-[#2D3354] text-white focus:border-[#63E6E2]"
                  data-testid="next-payday-input"
                />
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#63E6E2]/10 flex items-center justify-center">
                <Receipt className="w-8 h-8 text-[#63E6E2]" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">Your Bills</h2>
              <p className="text-[#9CA3AF]">Add your recurring bills (optional)</p>
            </div>

            {/* Existing Bills */}
            {bills.length > 0 && (
              <div className="space-y-2 mb-4">
                {bills.map((bill, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#1B1F3A] rounded-lg border border-[#2D3354]">
                    <div>
                      <p className="text-white font-medium">{bill.name}</p>
                      <p className="text-[#6B7280] text-sm">Due: {bill.due_date}th</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white font-medium">£{bill.amount.toFixed(2)}</span>
                      <button
                        onClick={() => removeBill(index)}
                        className="p-1 text-[#6B7280] hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Bill Form */}
            <div className="space-y-3 p-4 bg-[#1B1F3A] rounded-xl border border-[#2D3354]">
              <Input
                placeholder="Bill name (e.g., Rent, Netflix)"
                value={newBill.name}
                onChange={(e) => setNewBill({ ...newBill, name: e.target.value })}
                className="h-10 bg-[#252A4A] border-[#2D3354] text-white placeholder:text-[#6B7280]"
                data-testid="bill-name-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">£</span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Amount"
                    value={newBill.amount}
                    onChange={(e) => setNewBill({ ...newBill, amount: e.target.value })}
                    className="pl-7 h-10 bg-[#252A4A] border-[#2D3354] text-white placeholder:text-[#6B7280]"
                    data-testid="bill-amount-input"
                  />
                </div>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  placeholder="Due date (1-31)"
                  value={newBill.due_date}
                  onChange={(e) => setNewBill({ ...newBill, due_date: e.target.value })}
                  className="h-10 bg-[#252A4A] border-[#2D3354] text-white placeholder:text-[#6B7280]"
                  data-testid="bill-due-date-input"
                />
              </div>
              <Button
                type="button"
                onClick={addBill}
                variant="outline"
                className="w-full h-10 border-[#2D3354] text-white hover:bg-white/5"
                data-testid="add-bill-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Bill
              </Button>
            </div>

            <p className="text-[#6B7280] text-sm text-center">
              You can always add more bills later
            </p>
          </motion.div>
        );

      case 4:
        const totalBills = bills.reduce((sum, b) => sum + b.amount, 0);
        const availableAfterBills = parseFloat(balance || 0) - totalBills;
        const daysUntilPayday = nextPayday ? 
          Math.max(Math.ceil((new Date(nextPayday) - new Date()) / (1000 * 60 * 60 * 24)), 1) : 1;
        const safeToSpend = Math.max(availableAfterBills / daysUntilPayday, 0);

        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#63E6E2]/10 flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-[#63E6E2]" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">You're All Set!</h2>
              <p className="text-[#9CA3AF]">Here's your daily spending budget</p>
            </div>

            <div className="bg-[#1B1F3A] rounded-2xl border border-[#2D3354] p-6 text-center">
              <p className="text-[#6B7280] text-xs uppercase tracking-wider mb-2">Safe to Spend Per Day</p>
              <p className="font-heading text-5xl font-black text-[#63E6E2] mb-4">
                £{safeToSpend.toFixed(2)}
              </p>
              
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-[#2D3354]">
                <div>
                  <p className="text-[#6B7280] text-xs mb-1">Balance</p>
                  <p className="text-white font-semibold">£{parseFloat(balance || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[#6B7280] text-xs mb-1">Bills</p>
                  <p className="text-white font-semibold">£{totalBills.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[#6B7280] text-xs mb-1">Days</p>
                  <p className="text-white font-semibold">{daysUntilPayday}</p>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                notificationStep === 'success' ? 'bg-[#22C55E]/10' : 'bg-[#63E6E2]/10'
              }`}>
                {notificationStep === 'success' ? (
                  <CheckCircle className="w-8 h-8 text-[#22C55E]" />
                ) : (
                  <Bell className="w-8 h-8 text-[#63E6E2]" />
                )}
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">
                {notificationStep === 'success' ? 'Notifications Enabled!' : 'Enable Notifications'}
              </h2>
              <p className="text-[#9CA3AF]">
                {notificationStep === 'success' 
                  ? "You'll receive daily spending reminders" 
                  : 'Get daily reminders at 8 AM, 12 PM, 6 PM & 10 PM'}
              </p>
            </div>

            {/* Initial State - Show device instructions */}
            {(notificationStep === 'initial' || notificationStep === 'checking' || notificationStep === 'blocked') && (
              <div className="space-y-4">
                {/* What you'll get */}
                <div className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4">
                  <p className="text-white font-medium mb-3">What you'll receive:</p>
                  <ul className="space-y-2 text-sm text-[#9CA3AF]">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#63E6E2] flex-shrink-0" />
                      Morning: Your safe-to-spend amount
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#63E6E2] flex-shrink-0" />
                      Midday & Evening: Spending log reminders
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#63E6E2] flex-shrink-0" />
                      Bill due date alerts
                    </li>
                  </ul>
                </div>

                {/* Device Settings Instructions */}
                <div className="bg-[#252A4A] rounded-xl border border-[#2D3354] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Smartphone className="w-5 h-5 text-[#63E6E2]" />
                    <p className="text-[#63E6E2] font-medium">
                      {isIOS ? 'iPhone Setup:' : 'Device Setup:'}
                    </p>
                  </div>
                  
                  {isIOS ? (
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#63E6E2] flex items-center justify-center text-[#0F1224] text-xs font-bold flex-shrink-0">1</div>
                        <span className="text-[#9CA3AF]">Open your iPhone <strong className="text-white">Settings</strong> app</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#63E6E2] flex items-center justify-center text-[#0F1224] text-xs font-bold flex-shrink-0">2</div>
                        <span className="text-[#9CA3AF]">Tap <strong className="text-white">Notifications</strong></span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#63E6E2] flex items-center justify-center text-[#0F1224] text-xs font-bold flex-shrink-0">3</div>
                        <span className="text-[#9CA3AF]">Find and tap <strong className="text-white">Payday Control</strong></span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#63E6E2] flex items-center justify-center text-[#0F1224] text-xs font-bold flex-shrink-0">4</div>
                        <span className="text-[#9CA3AF]">Turn on <strong className="text-white">Allow Notifications</strong></span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#63E6E2] flex items-center justify-center text-[#0F1224] text-xs font-bold flex-shrink-0">1</div>
                        <span className="text-[#9CA3AF]">Open your device <strong className="text-white">Settings</strong></span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#63E6E2] flex items-center justify-center text-[#0F1224] text-xs font-bold flex-shrink-0">2</div>
                        <span className="text-[#9CA3AF]">Go to <strong className="text-white">Apps & Notifications</strong></span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#63E6E2] flex items-center justify-center text-[#0F1224] text-xs font-bold flex-shrink-0">3</div>
                        <span className="text-[#9CA3AF]">Find <strong className="text-white">Payday Control</strong></span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#63E6E2] flex items-center justify-center text-[#0F1224] text-xs font-bold flex-shrink-0">4</div>
                        <span className="text-[#9CA3AF]">Enable <strong className="text-white">Notifications</strong></span>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleEnableNotifications}
                  disabled={notificationStep === 'checking'}
                  className="w-full h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
                  data-testid="enable-notifications-btn"
                >
                  {notificationStep === 'checking' ? (
                    <>
                      <Bell className="w-5 h-5 mr-2 animate-pulse" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Bell className="w-5 h-5 mr-2" />
                      I've Enabled Notifications
                    </>
                  )}
                </Button>
                
                <button
                  onClick={() => setNotificationStep('skipped')}
                  className="w-full text-[#6B7280] text-sm hover:text-white transition-colors"
                >
                  Skip for now
                </button>
              </div>
            )}

            {/* Success State */}
            {notificationStep === 'success' && (
              <div className="text-center py-4">
                <div className="bg-[#1B1F3A] rounded-xl border border-[#22C55E]/30 p-6">
                  <CheckCircle className="w-12 h-12 text-[#22C55E] mx-auto mb-3" />
                  <p className="text-white font-medium mb-1">You're all set!</p>
                  <p className="text-[#6B7280] text-sm">
                    You'll receive reminders at 8 AM, 12 PM, 6 PM & 10 PM
                  </p>
                </div>
              </div>
            )}

            {/* Skipped State */}
            {notificationStep === 'skipped' && (
              <div className="text-center py-4">
                <div className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-6">
                  <Bell className="w-12 h-12 text-[#6B7280] mx-auto mb-3" />
                  <p className="text-white font-medium mb-1">No problem!</p>
                  <p className="text-[#6B7280] text-sm">
                    You can enable notifications anytime in Settings
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1224] flex flex-col">
      {/* Header */}
      <header className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-64.png" alt="Payday Control" className="w-10 h-10 rounded-xl" />
            <span className="font-heading font-bold text-lg text-white">Payday Control</span>
          </div>
          <span className="text-[#6B7280] text-sm">Step {step} of {totalSteps}</span>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4 h-1 bg-[#2D3354] rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#63E6E2] transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="p-4 sm:p-6 border-t border-[#2D3354]">
        <div className="max-w-md mx-auto flex items-center justify-between gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep(step - 1)}
            disabled={step === 1}
            className="border-[#2D3354] text-white hover:bg-white/5 disabled:opacity-50"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          {step < totalSteps ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold disabled:opacity-50"
              data-testid="next-btn"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleComplete}
              disabled={loading}
              className="bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
              data-testid="complete-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                'Get Started'
              )}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
